import React, { useState } from "react";
import styled from "styled-components";
import { Formik, FormikHelpers } from "formik";
import { head, pick } from "lodash";
import { FileInput } from "@sonder/ui/source/styled/components/FileInput";
import { Image } from "@sonder/ui/source/styled/components/Image";
import {
  Modal,
  ModalHeader,
  ModalProps,
} from "@sonder/ui/source/styled/components/Modal";
import { InputField } from "@sonder/ui/source/styled/fragments/InputField";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import { Label } from "@sonder/ui/source/styled/fragments/Label";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { ModalFooter } from "@sonder/ui/source/styled/fragments/Modal";
import { Placeholder } from "@sonder/ui/source/styled/fragments/Placeholder";
import { PrimaryButton } from "@sonder/ui/source/styled/fragments/PrimaryButton";
import { DangerousValidationException } from "@sonder/ui/source/styled/fragments/ValidationException";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { isOktaEnabled } from "auth/utils/oktaAuth";
import { FormikInputField } from "core/components/formik";
import { ONE_MEGABYTE } from "core/constants";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import { useUpdateUser } from "core/queries/UpdateUser";
import { useUploadFiles } from "core/queries/UploadFiles";
import { isRequired } from "core/validations";
import { FilesAttachable } from "typings/globalGQLTypes";

const SEGMENT_COMPONENT_NAME = "edit_profile_modal";

const StyledImage = styled(Image)`
  margin-bottom: 1rem;
`;

const EditProfileForm = styled.form`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const ProfileDetailsGroup = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;

  ${InputGroup} {
    align-items: flex-start;
    flex-wrap: wrap;
  }
`;

const UploadPhotoField = styled(InputField)`
  flex: 1 0 9.25rem;
`;

const StyledFileOptions = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const StyledFileInput = styled(FileInput)`
  align-items: center;
  border: 1px solid ${colors.blue7};
  border-radius: 0.3rem;
  color: ${colors.blue7};
  cursor: pointer;
  height: 2.3rem;
  margin-bottom: 0.5rem;
  padding: 0 0.7rem;
`;

interface EditProfileModalProps extends Pick<ModalProps, "isOpen" | "onClose"> {
  maxProfilePicUploadSize?: number;
  user: User;
}

type UserFormValues = Pick<User, "firstName" | "lastName">;
const getUserFormValues = (user: User) => pick(user, ["firstName", "lastName"]);

export const EditProfileModal = ({
  isOpen,
  maxProfilePicUploadSize = 4,
  onClose,
  user,
}: EditProfileModalProps) => {
  const [imageValidationException, setImageValidationException] = useState("");
  const errorReporter = useErrorReporterFiltered();
  const { track } = useSegment();
  const userId = user.id.split("|").pop();
  const [onUploadFiles, { loading: isUploading, error: uploadError }] =
    useUploadFiles({
      awaitRefetchQueries: true,
      refetchQueries: ["GetLoggedInUser"],
    });
  const [onUpdateUser, { loading: isUpdating }] = useUpdateUser({
    awaitRefetchQueries: true,
    refetchQueries: ["GetLoggedInUser"],
  });

  const onSubmit = async (
    { firstName, lastName }: UserFormValues,
    { setSubmitting }: FormikHelpers<UserFormValues>,
  ) => {
    try {
      if (!userId) return;

      // Reset submitting state
      setSubmitting(false);
      track({
        eventName: SEGMENT_COMPONENT_NAME,
        action: "click",
        element: "edit_profile_modal.submit",
        props: { userId },
      });
      await onUpdateUser({ variables: { id: userId, firstName, lastName } });
      track({
        eventName: SEGMENT_COMPONENT_NAME,
        action: "success",
        element: "edit_profile_modal.submit",
        props: {
          userId,
        },
      });
    } catch (error) {
      errorReporter.notify(error);
      track({
        eventName: SEGMENT_COMPONENT_NAME,
        action: "failure",
        element: "edit_profile_modal.submit",
        props: {
          userId,
        },
      });
    }
  };

  const onRemove = async () => {
    try {
      if (!userId) return;

      track({
        eventName: SEGMENT_COMPONENT_NAME,
        action: "click",
        element: "edit_profile_modal.remove_profile_pic",
        props: { userId },
      });

      await onUpdateUser({ variables: { id: userId, avatarId: null } });

      track({
        eventName: SEGMENT_COMPONENT_NAME,
        action: "success",
        element: "edit_profile_modal.remove_profile_pic",
        props: { userId },
      });
    } catch (error) {
      errorReporter.notify(error);
      track({
        eventName: SEGMENT_COMPONENT_NAME,
        action: "failure",
        element: "edit_profile_modal.remove_profile_pic",
        props: { userId },
      });
    }
  };

  const onUpload = async ({
    target: { validity, files },
  }: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = head(files);
      if (!userId || !validity.valid || !file) return;
      // Reset upload error state
      setImageValidationException("");

      // Check for max file size
      if (file.size > ONE_MEGABYTE * maxProfilePicUploadSize) {
        throw new Error(
          `File size must be less than ${maxProfilePicUploadSize}mb`,
        );
      }

      // Upload files
      await onUploadFiles({
        variables: {
          attachableId: userId,
          attachableType: FilesAttachable.user,
          files: [file],
        },
      });

      // Track successful upload
      track({
        eventName: SEGMENT_COMPONENT_NAME,
        action: "success",
        element: "edit_profile_modal.profile_pic_upload",
        props: {
          userId,
          nextValue: file.name,
        },
      });
    } catch (error) {
      errorReporter.notify(error);
      setImageValidationException(error.message);
      track({
        eventName: SEGMENT_COMPONENT_NAME,
        action: "failure",
        element: "edit_profile_modal.profile_pic_upload",
        props: { userId },
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {({ closeModal }) => (
        <>
          <ModalHeader title="Edit Profile" onClose={closeModal} />
          <Formik initialValues={getUserFormValues(user)} onSubmit={onSubmit}>
            {({ dirty, handleBlur, handleSubmit, isSubmitting, isValid }) => (
              <EditProfileForm onSubmit={handleSubmit}>
                <ProfileDetailsGroup>
                  <InputGroup>
                    <FormikInputField
                      required
                      label="First Name"
                      name="firstName"
                      placeholder="Enter First Name"
                      type="string"
                      validate={isRequired}
                    />
                    <FormikInputField
                      required
                      label="Last Name"
                      name="lastName"
                      placeholder="Enter Last Name"
                      type="string"
                      validate={isRequired}
                    />
                  </InputGroup>
                  {!isOktaEnabled && (
                    <InputField>
                      <Label>Password Reset</Label>
                      <div>
                        Open this{" "}
                        <Link href={`${ADMIN_HOST}/users/password/new`}>
                          link
                        </Link>{" "}
                        in incognito.
                      </div>
                    </InputField>
                  )}
                </ProfileDetailsGroup>
                <UploadPhotoField>
                  <Label>Profile Photo</Label>
                  {user.avatarUrl ? (
                    <StyledImage src={user.avatarUrl} />
                  ) : (
                    <Placeholder />
                  )}
                  <StyledFileOptions>
                    {!user.avatarUrl && (
                      <StyledFileInput
                        accept="image/png,image/jpeg,image/gif"
                        disabled={isUploading}
                        name="image"
                        onBlur={handleBlur}
                        onChange={onUpload}
                      >
                        {isUploading ? "Uploading..." : "Upload Photo"}
                      </StyledFileInput>
                    )}
                    {user.avatarUrl && <Link onClick={onRemove}>Remove</Link>}
                  </StyledFileOptions>
                  {imageValidationException && (
                    <DangerousValidationException
                      message={imageValidationException}
                    />
                  )}
                </UploadPhotoField>
                {uploadError && uploadError.message && (
                  <DangerousValidationException
                    message={uploadError.toString()}
                  />
                )}
                <ModalFooter>
                  <PrimaryButton
                    disabled={
                      isSubmitting ||
                      isUpdating ||
                      isUploading ||
                      !isValid ||
                      !dirty
                    }
                    type="submit"
                  >
                    {isSubmitting || isUpdating ? "Saving..." : "Save"}
                  </PrimaryButton>
                </ModalFooter>
              </EditProfileForm>
            )}
          </Formik>
        </>
      )}
    </Modal>
  );
};
